/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { StyleSheet, css } from 'aphrodite';
import { BrandColorKey, BrandColors, Header, LinkButton, LinkButtonWrapper } from '@blend/enterprise-components';
import { PENDING_FEE_NOTICE_COPY } from '../../constants';
import { Pricing } from 'blb-types/pricing';
import { useContext } from 'react';
import { FeesContext } from '../../containers/FeesTab';
import { FeeFormValue } from '../../types';
import { getMoreInfoRequestCopy } from '../../utils/ledgerUtils';

const styles = StyleSheet.create({
  backgroundMoreInfoRequested: {
    backgroundColor: BrandColors.ACCENT_YELLOW_07,
  },
  backgroundPending: {
    backgroundColor: BrandColors.ACCENT_GREEN_07,
  },
  moreInfoRequestedCopy: {
    alignItems: 'center',
    borderLeft: `1px solid ${BrandColors.ACCENT_ORANGE}`,
    display: 'flex',
    height: 15,
    marginLeft: 12,
    paddingLeft: 10,
    textTransform: 'initial',
    whiteSpace: 'pre-wrap',
  },
  moreInfoRequestedCopyWrapper: {
    alignItems: 'center',
    display: 'flex',
  },
  pendingFeeNoticeWrapper: {
    alignItems: 'center',
    display: 'flex',
    height: 28,
    justifyContent: 'space-between',
    padding: '6px 10px',
  },
});

const COLORS = {
  moreInfoRequest: {
    background: styles.backgroundMoreInfoRequested,
    font: 'ACCENT_ORANGE' as BrandColorKey,
  },
  pending: {
    background: styles.backgroundPending,
    font: 'ACCENT_GREEN' as BrandColorKey,
  },
};

export interface PendingFeeNoticeProps {
  cancelRequest: () => void;
  fee: FeeFormValue;
  updateType: Pricing.UpdateType;
}

export const PendingFeeNotice: React.FC<PendingFeeNoticeProps> = ({ updateType, cancelRequest, fee }) => {
  const { actionsEnabled } = useContext(FeesContext);

  const getColor = () => (fee.moreInfoRequested ? COLORS.moreInfoRequest : COLORS.pending);
  const getPendingFeeNoticeCopy = () => PENDING_FEE_NOTICE_COPY[updateType];
  const getMoreInfoRequestNoticeCopy = () => {
    return (
      <div className={css(styles.moreInfoRequestedCopyWrapper)}>
        NEED MORE INFO
        <span className={css(styles.moreInfoRequestedCopy)}>{` Fee amount: `}</span>
        {getMoreInfoRequestCopy(fee)}
      </div>
    );
  };

  return (
    <div className={css(styles.pendingFeeNoticeWrapper, getColor().background)} data-it="PendingFeeNotice">
      <Header colorName={getColor().font} fontWeight="BOLD" size={6}>
        {fee.moreInfoRequested ? getMoreInfoRequestNoticeCopy() : getPendingFeeNoticeCopy()}
      </Header>

      {updateType !== Pricing.UpdateType.ADD && (
        <LinkButtonWrapper>
          <LinkButton
            btnSize="sm"
            disabled={!actionsEnabled}
            icon="delete_forever"
            id="it-CancelRequest-FeeButton"
            label="Cancel Request"
            onClick={() => cancelRequest()}
          />
        </LinkButtonWrapper>
      )}
    </div>
  );
};
